'use client';

import { useCallback, useEffect, useMemo } from 'react';

import { useToast } from '@/shared/components/providers/ToastProvider';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
  desvincularSituacaoArbitral,
  fetchSituacoesDisponiveis,
  selectGerenciandoSituacao,
  selectProcessoArbitral,
  selectSituacoesDisponiveis,
  vincularSituacaoArbitral,
} from '../store';
import type { SituacaoAtiva } from '../types/arbitralDetailTypes';

interface UseArbitralSituacoesParams {
  processoId: string;
  canManage: boolean;
}

export function useArbitralSituacoes({ processoId, canManage }: UseArbitralSituacoesParams) {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const processo = useAppSelector(selectProcessoArbitral);
  const disponiveis = useAppSelector(selectSituacoesDisponiveis);
  const gerenciandoSituacao = useAppSelector(selectGerenciandoSituacao);

  useEffect(() => {
    if (canManage) {
      void dispatch(fetchSituacoesDisponiveis());
    }
  }, [canManage, dispatch]);

  const situacoesParaAdicionar = useMemo<SituacaoAtiva[]>(() => {
    const ativasIds = new Set((processo?.situacoes ?? []).map((situacao) => situacao.id));
    return disponiveis.filter((situacao) => !ativasIds.has(situacao.id));
  }, [processo?.situacoes, disponiveis]);

  const adicionarSituacao = useCallback(
    async (situacaoId: string) => {
      const result = await dispatch(vincularSituacaoArbitral({ processoId, situacaoId }));
      if (vincularSituacaoArbitral.fulfilled.match(result)) {
        toast.showSuccess('Situação adicionada');
        return;
      }
      toast.showError('Erro', result.payload || 'Não foi possível adicionar a situação');
    },
    [dispatch, processoId, toast],
  );

  const removerSituacao = useCallback(
    async (situacaoId: string) => {
      const result = await dispatch(desvincularSituacaoArbitral({ processoId, situacaoId }));
      if (desvincularSituacaoArbitral.fulfilled.match(result)) {
        toast.showSuccess('Situação removida');
        return;
      }
      toast.showError('Erro', result.payload || 'Não foi possível remover a situação');
    },
    [dispatch, processoId, toast],
  );

  return {
    situacoesParaAdicionar,
    gerenciandoSituacao,
    adicionarSituacao,
    removerSituacao,
  };
}
